import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, X, Trash2, FileDown, CircleCheck, Receipt, Search, Archive, Ban, Landmark, Banknote, CreditCard, Copy } from "lucide-react";
import api, { formatErr, API } from "../lib/api";
import { confirmDialog } from "../lib/confirm";
import { AdminPinPromptDialog } from "../components/admin/AdminPinPromptDialog";
import { BRAND_COLORS, withAlpha, STATUS_COLORS } from "../lib/bookingStatus";
import { deDateMedium } from "../lib/dateFormat";
import { deNumber } from "../lib/numberFormat";

const fmtDate = (iso) => iso ? deDateMedium(iso) : "";
const fmtMoney = (n, cur = "EUR") => `${deNumber(n || 0, 2)} ${cur}`;

const PAYMENT_METHODS = [
  { key: "bank_transfer", label: "Überweisung", icon: Landmark },
  { key: "cash",          label: "Bar",         icon: Banknote },
  { key: "credit_card",   label: "Kreditkarte", icon: CreditCard },
];
const paymentMethodLabel = (m) => (PAYMENT_METHODS.find((x) => x.key === m) || {}).label || "";
const todayIso = () => new Date().toISOString().slice(0, 10);
// Stable client-side id per invoice line so React keys survive add/remove
// (never serialized — buildPayload maps only description/qty/unit_price).
const _lid = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));

function PaymentModal({ open, invoice, onClose, onConfirm }) {
  const [paidAt, setPaidAt] = useState(todayIso);
  const [method, setMethod] = useState("bank_transfer");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setPaidAt(todayIso()); setMethod("bank_transfer"); }
  }, [open, invoice?.id]);
  if (!open || !invoice) return null;
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm({ paid_at: paidAt, payment_method: method }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-navy/70 p-0 md:p-6" onClick={onClose} data-testid="payment-modal">
      <div className="bg-white w-full md:max-w-md md:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10">
          <div>
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Zahlungseingang buchen</div>
            <h2 className="font-display text-2xl text-navy mt-1">Rechnung {invoice.number}</h2>
          </div>
          <button onClick={onClose} data-testid="payment-modal-close" aria-label="Schließen"><X className="w-5 h-5 text-navy" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <label htmlFor="paid-at" className="block text-[11px] text-navy-dark/60 mb-1">Zahldatum</label>
            <input id="paid-at" type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} max={todayIso()}
              data-testid="payment-paid-at"
              className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
          </div>
          <div>
            <div className="text-[11px] text-navy-dark/60 mb-2">Zahlart</div>
            <div className="grid grid-cols-3 gap-2" data-testid="payment-methods">
              {PAYMENT_METHODS.map((m) => {
                const Icon = m.icon;
                const active = method === m.key;
                return (
                  <button key={m.key} type="button" onClick={() => setMethod(m.key)}
                    data-testid={`payment-method-${m.key}`}
                    className={`flex flex-col items-center gap-1.5 px-3 py-4 border transition-colors ${
                      active ? "border-navy bg-navy text-ivory" : "border-navy/20 text-navy-dark hover:border-gold"
                    }`}
                    style={active ? { background: BRAND_COLORS.navy } : {}}
                  >
                    <Icon className="w-5 h-5" strokeWidth={1.6} />
                    <span className="text-[11px] tracking-wider uppercase">{m.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-3 bg-ivory/60 border border-navy/10 text-[12px] text-navy-dark/70 leading-relaxed">
            Betrag: <b className="text-navy">{deNumber(invoice.total || 0, 2)} {invoice.currency || "EUR"}</b>
            <br />Kunde: {invoice.customer_name || invoice.recipient_name || "—"}
          </div>
        </div>
        <div className="px-5 py-4 border-t border-navy/10 flex justify-end gap-3">
          <button onClick={onClose} disabled={busy} className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5">Abbrechen</button>
          <button onClick={confirm} disabled={busy} data-testid="payment-confirm-btn"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}>
            {busy ? "Wird gebucht …" : "Zahlung buchen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid:      { label: "Bezahlt", bg: STATUS_COLORS.green, fg: "#fff" },
    sent:      { label: "Offen",   bg: BRAND_COLORS.gold, fg: BRAND_COLORS.navy },
    overdue:   { label: "Überfällig", bg: STATUS_COLORS.red, fg: "#fff" },
    draft:     { label: "Entwurf", bg: "#e5e7eb", fg: "#374151" },
    cancelled: { label: "Storniert", bg: "#5A6478", fg: "#fff" },
  };
  const s = map[status] || map.sent;
  return (
    <span data-testid={`team-invoice-status-${status}`}
      className="inline-flex items-center gap-1 px-2.5 py-1 text-[10.5px] tracking-wider uppercase rounded-full shrink-0"
      style={{ background: s.bg, color: s.fg }}>
      {status === "paid" && <CircleCheck className="w-3 h-3" strokeWidth={2} />}
      {status === "cancelled" && <Ban className="w-3 h-3" strokeWidth={2} />}
      {s.label}
    </span>
  );
}

function CreateInvoiceModal({ open, onClose, customers, onCreated }) {
  // Empfänger + Kernfelder
  const [customerId, setCustomerId] = useState("");
  // Feb 2026 · 9 Sep 2026 · User-Directive „Auto-Fill Rechnungsadresse":
  // Admin wählt Kunde UND Objekt → objektspezifische Rechnungsadresse
  // wird automatisch in die Empfänger-Zeilen übernommen. Feld bleibt
  // manuell editierbar. Wir tracken die letzte Auto-Fill-Value via Ref,
  // damit ein manuell überschriebener Text nicht bei jedem Selector-
  // Wechsel weggewischt wird.
  const [propertyId, setPropertyId] = useState("");
  const [customerBilling, setCustomerBilling] = useState(null);
  const [customerProperties, setCustomerProperties] = useState([]);
  const autoFilledRef = useRef("");
  const [customerNumber, setCustomerNumber] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress, setRecipientAddressRaw] = useState(""); // free-text multiline
  const setRecipientAddress = (val) => {
    setRecipientAddressRaw(val);
    // Jede manuelle Änderung „entkoppelt" das Feld vom Auto-Fill —
    // damit der nächste Objekt-Wechsel den Text nicht überschreibt.
    if (val !== autoFilledRef.current) autoFilledRef.current = null;
  };
  const [salutation, setSalutation] = useState("Sehr geehrte Damen und Herren,");
  const [introText, setIntroText] = useState(
    "vielen Dank, dass Sie die Services von Weissblom genutzt haben. " +
    "Hierfür berechnen wir Ihnen folgende Leistungen."
  );
  // Rechnungsangaben
  const [title, setTitle] = useState("");
  const [servicePeriodStart, setServicePeriodStart] = useState("");
  const [servicePeriodEnd, setServicePeriodEnd] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [vat, setVat] = useState(19);
  // Fußangaben
  const [locationOfService, setLocationOfService] = useState("");
  const [signatureName, setSignatureName] = useState("Babett Gehrmann");
  const [notes, setNotes] = useState("");
  // Positionen
  const [items, setItems] = useState([{ _uid: _lid(), description: "", qty: 1, unit_price: 0 }]);
  const [busy, setBusy] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  // Prefill recipient block whenever customer changes
  useEffect(() => {
    if (!open) return;
    if (!customerId && customers[0]) setCustomerId(customers[0].id);
  }, [open, customers, customerId]);

  useEffect(() => {
    if (!customerId) return;
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    // Only prefill if the field is still empty — never overwrite user input.
    setRecipientName((cur) => cur || c.name || c.email || "");
  }, [customerId, customers]);

  // Feb 2026 · 9 Sep 2026 · Fetch billing context (customer + properties)
  // whenever the selected customer changes. Powers the auto-fill of the
  // recipient address block. Detail-Endpoint liefert `customer` inkl.
  // `billing_address` und alle `properties` mit ihrer objektspezifischen
  // `billing_address`.
  useEffect(() => {
    if (!open || !customerId) {
      setCustomerBilling(null);
      setCustomerProperties([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/team/customers/${customerId}/detail`);
        if (cancelled) return;
        setCustomerBilling(data?.customer?.billing_address || null);
        const props = (data?.properties || []).filter((p) => !p.archived_at);
        setCustomerProperties(props);
        // Auto-Select: bevorzugt das Hauptobjekt, sonst das erste Objekt.
        // Legt nicht drauf, wenn der Admin schon manuell eins gewählt hat
        // und es zum neuen Kunden gehört.
        setPropertyId((cur) => {
          if (cur && props.some((p) => p.id === cur)) return cur;
          const primary = props.find((p) => p.is_primary);
          return primary?.id || props[0]?.id || "";
        });
      } catch (err) {
        console.debug("team-invoice customer detail fetch failed:", err?.response?.status);
        if (!cancelled) { setCustomerBilling(null); setCustomerProperties([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [open, customerId]);

  // Feb 2026 · 9 Sep 2026 · Auto-Fill der Rechnungsadresse-Zeilen aus dem
  // ausgewählten Objekt (Fallback: Kunden-Standardadresse). Überschreibt
  // NUR, wenn der Admin die Zeilen nicht manuell verändert hat
  // (`autoFilledRef.current` matched den aktuellen Wert). Der aktuelle
  // Feldwert wird per Ref gelesen, damit Tippen den Effekt nicht triggert.
  const recipientAddressRef = useRef(recipientAddress);
  useEffect(() => { recipientAddressRef.current = recipientAddress; });
  useEffect(() => {
    if (!open) return;
    const activeProp = customerProperties.find((p) => p.id === propertyId);
    const ba = activeProp?.billing_address || customerBilling || null;
    if (!ba) return;
    const lines = [
      (ba.street || "").trim(),
      `${(ba.zip || "").trim()} ${(ba.city || "").trim()}`.trim(),
      (ba.country || "").trim(),
    ].filter(Boolean);
    if (!lines.length) return;
    const nextText = lines.join("\n");
    // Nicht überschreiben, wenn Admin selbst editiert hat.
    const current = recipientAddressRef.current;
    if (current && current !== autoFilledRef.current) return;
    autoFilledRef.current = nextText;
    setRecipientAddressRaw(nextText);
  }, [open, propertyId, customerBilling, customerProperties]);

  const reset = () => {
    setCustomerId(customers[0]?.id || "");
    setPropertyId("");
    setCustomerBilling(null);
    setCustomerProperties([]);
    autoFilledRef.current = "";
    setCustomerNumber(""); setRecipientName(""); setRecipientAddressRaw("");
    setSalutation("Sehr geehrte Damen und Herren,");
    setIntroText(
      "vielen Dank, dass Sie die Services von Weissblom genutzt haben. " +
      "Hierfür berechnen wir Ihnen folgende Leistungen."
    );
    setTitle(""); setServicePeriodStart(""); setServicePeriodEnd(""); setDueDate("");
    setVat(19); setLocationOfService(""); setSignatureName("Babett Gehrmann");
    setNotes(""); setItems([{ _uid: _lid(), description: "", qty: 1, unit_price: 0 }]);
  };
  const close = () => { reset(); onClose(); };

  const updItem = (i, key, val) => setItems((cur) => cur.map((it, idx) => idx === i ? { ...it, [key]: val } : it));
  const addItem = () => setItems((cur) => [...cur, { _uid: _lid(), description: "", qty: 1, unit_price: 0 }]);
  const removeItem = (i) => setItems((cur) => cur.length === 1 ? cur : cur.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit_price) || 0), 0);
  const vatAmount = subtotal * (Number(vat) || 0) / 100;
  const total = subtotal + vatAmount;

  // Feb 2026 · 9 Sep 2026 · Shared payload builder — save + preview
  // müssen exakt denselben Payload liefern, damit die Vorschau garantiert
  // dem finalen PDF entspricht.
  const buildPayload = () => ({
    customer_id: customerId,
    property_id: propertyId || null,
    title: title.trim(),
    vat_rate: Number(vat) || 0,
    due_date: dueDate || null,
    service_period_start: servicePeriodStart || null,
    service_period_end: servicePeriodEnd || null,
    customer_number: customerNumber.trim() || null,
    recipient_name: recipientName.trim() || null,
    recipient_address_lines: recipientAddress
      .split(/\r?\n/).map((l) => l.trim()).filter(Boolean),
    salutation: salutation.trim() || null,
    intro_text: introText.trim() || null,
    location_of_service: locationOfService.trim() || null,
    signature_name: signatureName.trim() || null,
    notes: notes.trim() || null,
    line_items: items
      .filter((it) => it.description.trim())
      .map((it) => ({
        description: it.description.trim(),
        qty: Number(it.qty) || 1,
        unit_price: Number(it.unit_price) || 0,
      })),
  });

  // Feb 2026 · 9 Sep 2026 · User-Directive „PDF-Vorschau": rendert die
  // Rechnung ohne sie zu speichern, öffnet das PDF in einem neuen Tab.
  // Damit sieht der Admin die automatische Adress-Übernahme im finalen
  // Layout, bevor er auf „Rechnung erstellen & senden" klickt.
  const openPreview = async () => {
    if (!customerId) { toast.error("Bitte zuerst einen Kunden auswählen."); return; }
    const payload = buildPayload();
    if (!payload.line_items.length) {
      toast.error("Mindestens eine Position mit Beschreibung ist erforderlich.");
      return;
    }
    setPreviewBusy(true);
    try {
      const resp = await api.post("/invoices/preview-pdf", payload, {
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener");
      if (!win) toast.info("Bitte Pop-ups für die Vorschau erlauben.");
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      // Blob-Response bei Fehler: JSON aus Blob rausparsen
      let detail = err.message;
      try {
        const text = await err.response?.data?.text?.();
        if (text) detail = JSON.parse(text)?.detail || text;
      } catch { /* fall through */ }
      toast.error(formatErr(detail) || err.message);
    } finally { setPreviewBusy(false); }
  };

  const save = async () => {
    if (!customerId) { toast.error("Bitte einen Kunden wählen."); return; }
    if (!title.trim()) { toast.error("Bitte einen Titel angeben."); return; }
    if (!items.some((it) => it.description.trim() && (Number(it.unit_price) || 0) > 0)) {
      toast.error("Mindestens eine Position mit Beschreibung und Preis erforderlich.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", buildPayload());
      toast.success(`Rechnung ${data.number} erstellt.`);
      onCreated(data);
      close();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-navy/70 p-0 md:p-6" onClick={close} data-testid="team-invoice-modal">
      <div className="bg-white w-full md:max-w-3xl md:rounded-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/10 sticky top-0 bg-white z-10">
          <div>
            <div className="label-caps" style={{ color: BRAND_COLORS.gold }}>Neue Rechnung</div>
            <h2 className="font-display text-2xl text-navy mt-1">Rechnung erstellen</h2>
          </div>
          <button onClick={close} aria-label="Schließen" data-testid="team-invoice-modal-close"><X className="w-5 h-5 text-navy" /></button>
        </div>

        <div className="p-5 space-y-6">
          {/* --- 1. Empfänger --- */}
          <section>
            <div className="label-caps mb-3">Empfänger</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="inv-customer" className="block text-[11px] text-navy-dark/60 mb-1">Kunde</label>
                <select id="inv-customer" data-testid="team-invoice-customer"
                  value={customerId} onChange={(e) => { setCustomerId(e.target.value); setPropertyId(""); }}
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark bg-white">
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
                </select>
              </div>
              <div>
                <label htmlFor="inv-property" className="block text-[11px] text-navy-dark/60 mb-1">
                  Objekt
                  {customerProperties.length > 0 && (
                    <span className="text-navy-dark/40 italic ml-1">
                      · übernimmt die Rechnungsadresse
                    </span>
                  )}
                </label>
                <select id="inv-property" data-testid="team-invoice-property"
                  value={propertyId} onChange={(e) => setPropertyId(e.target.value)}
                  disabled={customerProperties.length === 0}
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark bg-white disabled:opacity-60 disabled:cursor-not-allowed">
                  {customerProperties.length === 0 ? (
                    <option value="">Kein Objekt hinterlegt</option>
                  ) : (
                    <>
                      <option value="">— Ohne Objekt-Bindung —</option>
                      {customerProperties.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name || p.address || p.id}
                          {p.is_primary ? " · Hauptobjekt" : ""}
                          {p.billing_address ? " · eigene Rechnungsadresse" : ""}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
              <div>
                <label htmlFor="inv-cnum" className="block text-[11px] text-navy-dark/60 mb-1">
                  Kundennummer <span className="text-navy-dark/40 italic">(optional)</span>
                </label>
                <input id="inv-cnum" value={customerNumber} onChange={(e) => setCustomerNumber(e.target.value)}
                  placeholder="z. B. 251012" data-testid="team-invoice-customer-number"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="inv-rname" className="block text-[11px] text-navy-dark/60 mb-1">Anschrift-Name (fett auf PDF)</label>
                <input id="inv-rname" value={recipientName} onChange={(e) => setRecipientName(e.target.value)}
                  placeholder="z. B. Straßlach Living GmbH"
                  data-testid="team-invoice-recipient-name"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="inv-raddr" className="block text-[11px] text-navy-dark/60 mb-1 flex items-center gap-2 flex-wrap">
                  <span>Anschrift-Zeilen · eine pro Zeile</span>
                  {(() => {
                    const activeProp = customerProperties.find((p) => p.id === propertyId);
                    if (activeProp?.billing_address && recipientAddress === autoFilledRef.current) {
                      return (
                        <span
                          className="text-[9.5px] tracking-[0.14em] uppercase px-1.5 py-0.5 text-ivory"
                          style={{ background: BRAND_COLORS.gold }}
                          data-testid="team-invoice-address-source-property"
                          title="Aus objektspezifischer Rechnungsadresse übernommen · manuell überschreibbar."
                        >
                          Aus Objekt
                        </span>
                      );
                    }
                    if (!activeProp?.billing_address && customerBilling && recipientAddress === autoFilledRef.current) {
                      return (
                        <span
                          className="text-[9.5px] tracking-[0.14em] uppercase px-1.5 py-0.5 text-navy border border-navy/25"
                          data-testid="team-invoice-address-source-customer"
                          title="Aus Kunden-Standardadresse übernommen · manuell überschreibbar."
                        >
                          Aus Kunden-Standard
                        </span>
                      );
                    }
                    return null;
                  })()}
                </label>
                <textarea id="inv-raddr" rows={3} value={recipientAddress} onChange={(e) => setRecipientAddress(e.target.value)}
                  placeholder={"Zugspitzstraße 14\n82041 Oberbiberg\nDeutschland"}
                  data-testid="team-invoice-recipient-address"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark leading-relaxed" />
              </div>
            </div>
          </section>

          {/* --- 2. Rechnungsangaben --- */}
          <section>
            <div className="label-caps mb-3">Rechnungsangaben</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label htmlFor="inv-title" className="block text-[11px] text-navy-dark/60 mb-1">Titel</label>
                <input id="inv-title" value={title} onChange={(e) => setTitle(e.target.value)}
                  placeholder="z. B. Hausmeisterleistungen Januar–Juni 2026"
                  data-testid="team-invoice-title"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div>
                <label htmlFor="inv-lps" className="block text-[11px] text-navy-dark/60 mb-1">Leistungszeitraum · Start</label>
                <input id="inv-lps" type="date" value={servicePeriodStart} onChange={(e) => setServicePeriodStart(e.target.value)}
                  data-testid="team-invoice-period-start"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div>
                <label htmlFor="inv-lpe" className="block text-[11px] text-navy-dark/60 mb-1">Leistungszeitraum · Ende</label>
                <input id="inv-lpe" type="date" value={servicePeriodEnd} onChange={(e) => setServicePeriodEnd(e.target.value)}
                  data-testid="team-invoice-period-end"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div>
                <label htmlFor="inv-due" className="block text-[11px] text-navy-dark/60 mb-1">Fällig am <span className="text-navy-dark/40 italic">(optional)</span></label>
                <input id="inv-due" type="date" data-testid="team-invoice-due"
                  value={dueDate} onChange={(e) => setDueDate(e.target.value)}
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="inv-salut" className="block text-[11px] text-navy-dark/60 mb-1">Anrede</label>
                <input id="inv-salut" value={salutation} onChange={(e) => setSalutation(e.target.value)}
                  placeholder="Sehr geehrte Frau Muster,"
                  data-testid="team-invoice-salutation"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div className="md:col-span-3">
                <label htmlFor="inv-intro" className="block text-[11px] text-navy-dark/60 mb-1">Einleitungstext</label>
                <textarea id="inv-intro" rows={2} value={introText} onChange={(e) => setIntroText(e.target.value)}
                  data-testid="team-invoice-intro"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark leading-relaxed" />
              </div>
            </div>
          </section>

          {/* --- 3. Positionen --- */}
          <section>
            <div className="flex items-baseline justify-between mb-2">
              <div className="label-caps">Positionen</div>
              <button onClick={addItem} data-testid="team-invoice-add-item" className="text-[11px] tracking-wider uppercase text-navy border border-navy/25 px-3 py-1 hover:border-gold">+ Position</button>
            </div>
            <div className="grid grid-cols-[24px_1fr_70px_100px_100px_28px] gap-2 items-center pb-2 text-[10.5px] tracking-wider uppercase text-navy-dark/50">
              <span>Pos.</span>
              <span>Beschreibung</span>
              <span className="text-right">Menge</span>
              <span className="text-right">Einzelpreis</span>
              <span className="text-right">Gesamt</span>
              <span></span>
            </div>
            <div className="space-y-2">
              {items.map((it, i) => {
                const line = (Number(it.qty) || 0) * (Number(it.unit_price) || 0);
                return (
                  <div key={it._uid} className="grid grid-cols-[24px_1fr_70px_100px_100px_28px] gap-2 items-center" data-testid={`team-invoice-item-${i}`}>
                    <span className="text-[13px] text-navy-dark/60">{i + 1}.</span>
                    <input value={it.description} onChange={(e) => updItem(i, "description", e.target.value)}
                      placeholder="Beschreibung" data-testid={`team-invoice-item-desc-${i}`}
                      className="border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark" />
                    <input type="number" step="0.5" min="0" value={it.qty} onChange={(e) => updItem(i, "qty", e.target.value)}
                      placeholder="Menge" data-testid={`team-invoice-item-qty-${i}`}
                      className="border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark text-right" />
                    <input type="number" step="0.01" min="0" value={it.unit_price} onChange={(e) => updItem(i, "unit_price", e.target.value)}
                      placeholder="Einzelpreis" data-testid={`team-invoice-item-price-${i}`}
                      className="border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy-dark text-right" />
                    <div className="text-[13px] text-navy-dark text-right px-2" data-testid={`team-invoice-item-total-${i}`}>{fmtMoney(line)}</div>
                    <button onClick={() => removeItem(i)} disabled={items.length === 1} title="Entfernen"
                      className="text-navy-dark/50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Summen */}
            <div className="grid grid-cols-2 gap-4 items-end mt-4">
              <div>
                <label htmlFor="inv-vat" className="block text-[11px] text-navy-dark/60 mb-1">USt.-Satz (%)</label>
                <input id="inv-vat" type="number" step="0.1" min="0" value={vat} onChange={(e) => setVat(e.target.value)}
                  data-testid="team-invoice-vat"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div className="text-right space-y-1 text-[13px] text-navy-dark" data-testid="team-invoice-summary">
                <div className="flex justify-between"><span className="text-navy-dark/60">Gesamtbetrag netto:</span> <span data-testid="inv-summary-subtotal">{fmtMoney(subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-navy-dark/60">Umsatzsteuer {Number(vat) || 0} %:</span> <span data-testid="inv-summary-vat">{fmtMoney(vatAmount)}</span></div>
                <div className="flex justify-between font-display text-xl text-navy border-t border-gold/50 pt-1 mt-1"><span>Gesamtbetrag brutto:</span> <span data-testid="inv-summary-total">{fmtMoney(total)}</span></div>
              </div>
            </div>
          </section>

          {/* --- 4. Zusatz-Angaben --- */}
          <section>
            <div className="label-caps mb-3">Zusatz-Angaben</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label htmlFor="inv-los" className="block text-[11px] text-navy-dark/60 mb-1">Leistungsort <span className="text-navy-dark/40 italic">(optional)</span></label>
                <input id="inv-los" value={locationOfService} onChange={(e) => setLocationOfService(e.target.value)}
                  placeholder="z. B. Tölzer Straße 5, 82064 Straßlach Dingharting"
                  data-testid="team-invoice-location"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div>
                <label htmlFor="inv-sign" className="block text-[11px] text-navy-dark/60 mb-1">Unterschrift · Name</label>
                <input id="inv-sign" value={signatureName} onChange={(e) => setSignatureName(e.target.value)}
                  placeholder="z. B. Babett Gehrmann"
                  data-testid="team-invoice-signature"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
              <div>
                <label htmlFor="inv-notes" className="block text-[11px] text-navy-dark/60 mb-1">Interner Hinweis <span className="text-navy-dark/40 italic">(optional)</span></label>
                <input id="inv-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                  placeholder="Referenz, Buchungshinweis, …"
                  data-testid="team-invoice-notes"
                  className="w-full border border-navy/20 focus:border-gold focus:outline-none px-4 py-2.5 text-navy-dark" />
              </div>
            </div>
          </section>
        </div>

        <div className="px-5 py-4 border-t border-navy/10 flex justify-end gap-3 sticky bottom-0 bg-white flex-wrap">
          <button onClick={close} disabled={busy || previewBusy} className="text-[12px] tracking-wider uppercase text-navy-dark/70 hover:text-navy px-4 py-2.5">Abbrechen</button>
          {/* Feb 2026 · 9 Sep 2026 · „PDF-Vorschau" — outlined Sekundär-
              Button. Sitzt direkt vor dem primären „erstellen & senden",
              damit der Admin die Adress-Übernahme im finalen Layout
              verifizieren kann, bevor die Rechnungs-Nr. vergeben wird. */}
          <button onClick={openPreview} disabled={busy || previewBusy}
            data-testid="team-invoice-preview-btn"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase border border-navy hover:bg-navy hover:text-ivory transition-colors px-5 py-3 disabled:opacity-60"
            style={{ color: BRAND_COLORS.navy }}>
            {previewBusy ? "Wird gerendert…" : "PDF-Vorschau"}
          </button>
          <button onClick={save} disabled={busy || previewBusy} data-testid="team-invoice-save-btn"
            className="text-ivory px-6 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90 transition-opacity disabled:opacity-60"
            style={{ background: BRAND_COLORS.navy }}>
            {busy ? "Wird erstellt…" : "Rechnung erstellen & senden"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TeamInvoices() {
  const [rows, setRows] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  // Feb 2026 · 9 Sep 2026 · User-Directive „Der Admin kann eine Rechnung
  // als 'Bezahlt' markieren (mit PIN-Bestätigung)". Session-Unlock-Pattern
  // analog zu TeamProjects: erste „Bezahlt"-Aktion fragt den 4-stelligen
  // Admin-PIN ab, danach bleibt die Session bis zum Seiten-Reload frei —
  // der Admin muss die PIN nicht für jede einzelne Rechnung neu eingeben.
  const [pinUnlocked, setPinUnlocked] = useState(false);
  const [pinPendingInvoice, setPinPendingInvoice] = useState(null);

  // Rechnungs-Archiv: Live-Suche + Jahres-Gruppierung + Status-Filter + Monats-Zoom
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | open | paid
  const [searchParams, setSearchParams] = useSearchParams();
  const monthFilter = searchParams.get("month") || ""; // YYYY-MM · gesetzt vom Umsatz-Balken

  useEffect(() => {
    (async () => {
      try {
        const [inv, cust] = await Promise.all([
          api.get("/invoices"),
          api.get("/team/customers"),
        ]);
        setRows(inv.data);
        setCustomers(cust.data);
      } catch (err) {
        toast.error(formatErr(err.response?.data?.detail) || err.message);
        setRows([]);
      }
    })();
  }, []);

  const markPaid = async (inv) => {
    // PIN-Gate vor dem Öffnen des Zahlungs-Modals. Erste Rechnung im
    // Session-Kontext öffnet den PIN-Dialog; nach Erfolg wird
    // `pinUnlocked=true` gesetzt und weitere Rechnungen springen
    // direkt in die Zahlungsbuchung.
    if (!pinUnlocked) {
      setPinPendingInvoice(inv);
      return;
    }
    setPayTarget(inv);
  };
  const onPinSuccess = () => {
    setPinUnlocked(true);
    const inv = pinPendingInvoice;
    setPinPendingInvoice(null);
    if (inv) setPayTarget(inv);
  };
  const confirmPayment = async ({ paid_at, payment_method }) => {
    if (!payTarget) return;
    try {
      const { data } = await api.patch(`/invoices/${payTarget.id}/pay`, { paid_at, payment_method });
      setRows((cur) => cur.map((x) => x.id === payTarget.id ? { ...x, ...data } : x));
      toast.success(`Zahlung gebucht · ${paymentMethodLabel(payment_method)} · ${paid_at}.`);
      setPayTarget(null);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };
  const cancelInvoice = async (inv) => {
    if (inv.type === "credit_note") { toast.error("Eine Storno-Gutschrift kann nicht selbst storniert werden."); return; }
    if (inv.status === "cancelled") { toast.info("Diese Rechnung ist bereits storniert."); return; }
    if (!(await confirmDialog({
      title: `Rechnung ${inv.number} stornieren?`,
      description:
        "Es wird eine neue Storno-Gutschrift mit XR-Nummer erzeugt (invertierte Beträge). " +
        "Die Ursprungs-Rechnung wird als \u201eStorniert\u201c markiert, PDF und Nummer bleiben zu Prüfungszwecken erhalten. " +
        "Der Kunde erhält automatisch eine Push-Benachrichtigung mit der Gutschrift.",
      confirmText: "Storno-Gutschrift erzeugen",
      variant: "danger",
    }))) return;
    try {
      const { data: cn } = await api.post(`/invoices/${inv.id}/cancel`);
      setRows((cur) => {
        const withCancelled = cur.map((x) => x.id === inv.id
          ? { ...x, status: "cancelled", cancelled_at: cn.source_invoice_id ? new Date().toISOString() : x.cancelled_at, cancel_credit_note_id: cn.id }
          : x);
        return [{ ...cn, customer_name: inv.customer_name || "" }, ...withCancelled];
      });
      toast.success(`Storno-Gutschrift ${cn.number} erzeugt · Kunde wurde benachrichtigt.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };
  const del = async (inv) => {
    if (!(await confirmDialog({
      title: "Rechnung löschen?",
      description: `Rechnung ${inv.number} wird unwiderruflich entfernt.`,
      confirmText: "Löschen",
      variant: "danger",
    }))) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      setRows((cur) => cur.filter((x) => x.id !== inv.id));
      toast.success("Gelöscht.");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  // Feb 2026 · 9 Sep 2026 · User-Directive „Als Vorlage duplizieren":
  // Adressen, Kunde, Objekt und Positionen werden 1:1 übernommen, neue
  // Nr. + heutiges Datum, `paid_at`/Storno-Zustand werden zurückgesetzt.
  // Damit sind wiederkehrende Rechnungen (Concierge-Pauschale, Winter-
  // Service, jährliche Wartung) in einem Klick fertig.
  const duplicate = async (inv) => {
    if (inv.type === "credit_note") {
      toast.error("Storno-Gutschriften können nicht dupliziert werden.");
      return;
    }
    try {
      const { data } = await api.post(`/invoices/${inv.id}/duplicate`);
      const customer = customers.find((c) => c.id === data.customer_id);
      setRows((cur) => [{ ...data, customer_name: customer?.name || customer?.email || "" }, ...(cur || [])]);
      toast.success(`Kopie ${data.number} erstellt · als frische, unbezahlte Rechnung.`);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    }
  };

  // ---- Rechnungs-Archiv: Filter + Gruppierung nach Jahr, sortiert nach Datum (neueste zuerst) ----
  const grouped = useMemo(() => {
    if (!rows) return [];
    const q = query.trim().toLowerCase();
    const filtered = rows.filter((inv) => {
      if (statusFilter === "open" && inv.status === "paid") return false;
      if (statusFilter === "paid" && inv.status !== "paid") return false;
      if (monthFilter && (inv.issued_at || "").slice(0, 7) !== monthFilter) return false;
      if (!q) return true;
      const hay = [
        inv.number, inv.title, inv.customer_name, inv.customer_number,
        inv.recipient_name, inv.location_of_service,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
    const byYear = {};
    for (const inv of filtered) {
      const y = (inv.issued_at || "").slice(0, 4) || "—";
      (byYear[y] = byYear[y] || []).push(inv);
    }
    return Object.entries(byYear).sort(([a], [b]) => b.localeCompare(a));
  }, [rows, query, statusFilter, monthFilter]);

  const totalCount = rows?.length || 0;
  const shownCount = grouped.reduce((s, [, list]) => s + list.length, 0);

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10 md:py-14" data-testid="team-invoices">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="label-caps mb-3">Weissblom Team</div>
          <h1 className="font-display text-4xl md:text-5xl text-navy">Rechnungen</h1>
          <p className="text-[14px] text-navy-dark/70 mt-3 max-w-2xl">
            Rechnungen im Weissblom-Design erstellen · Live-PDF-Vorschau · sortiertes Archiv
            nach Jahr und Kunde · direkte Verfügbarkeit für den Kunden inkl. Push-Benachrichtigung.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          data-testid="team-invoice-add-btn"
          className="inline-flex items-center gap-2 text-ivory px-5 py-3 text-[11px] tracking-[0.18em] uppercase hover:opacity-90"
          style={{ background: BRAND_COLORS.navy }}>
          <Plus className="w-4 h-4" strokeWidth={1.6} /> Neue Rechnung
        </button>
      </div>

      {/* --- Archiv Header: Suche + Statusfilter --- */}
      <div className="bg-white border border-navy/10 p-4 md:p-5 mb-6 flex flex-wrap items-center gap-3" data-testid="team-invoices-archive-toolbar">
        <div className="flex items-center gap-2 min-w-[220px] flex-1">
          <Archive className="w-4 h-4 text-navy-dark/40" strokeWidth={1.5} />
          <span className="label-caps text-navy-dark/70 whitespace-nowrap">Rechnungs-Archiv</span>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[220px] max-w-[420px]">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-navy-dark/40" strokeWidth={1.5} />
            <input
              type="search"
              placeholder="Suche · Nummer, Kunde, Titel, Ort …"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="team-invoices-search"
              className="w-full pl-10 pr-3 py-2.5 border border-navy/15 focus:border-gold focus:outline-none text-[13px] text-navy-dark"
            />
          </div>
        </div>
        <div className="flex items-center gap-1 p-1 bg-ivory/60 border border-navy/10 rounded-full" data-testid="team-invoices-status-filter">
          {[
            { key: "all",  label: "Alle" },
            { key: "open", label: "Offen" },
            { key: "paid", label: "Bezahlt" },
          ].map((f) => (
            <button key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`team-invoices-status-${f.key}`}
              className={`px-3 py-1.5 text-[10.5px] tracking-[0.14em] uppercase rounded-full transition-colors ${
                statusFilter === f.key
                  ? "text-ivory"
                  : "text-navy-dark/60 hover:text-navy"
              }`}
              style={statusFilter === f.key ? { background: BRAND_COLORS.navy } : {}}
              aria-pressed={statusFilter === f.key}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="text-[11.5px] text-navy-dark/60 whitespace-nowrap" data-testid="team-invoices-count">
          {shownCount} von {totalCount} Rechnungen
        </div>
      </div>

      {monthFilter && (
        <div className="mb-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.14em] uppercase"
             style={{ background: withAlpha(BRAND_COLORS.gold, 0.14), color: BRAND_COLORS.navy, border: `1px solid ${withAlpha(BRAND_COLORS.gold, 0.4)}` }}
             data-testid="team-invoices-month-chip">
          Monat: {(() => {
            const [y, m] = monthFilter.split("-");
            const names = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
            return `${names[parseInt(m, 10) - 1]} ${y}`;
          })()}
          <button
            onClick={() => { const p = new URLSearchParams(searchParams); p.delete("month"); setSearchParams(p); }}
            data-testid="team-invoices-month-clear"
            className="hover:text-red-800 transition-colors"
            aria-label="Monats-Filter entfernen"
          >
            <X className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
        </div>
      )}

      {rows === null ? (
        <div className="bg-white border border-navy/10 p-8 text-center label-caps">Wird geladen …</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-dashed border-navy/20 p-12 text-center">
          <Receipt className="w-8 h-8 mx-auto mb-3" style={{ color: BRAND_COLORS.gold }} strokeWidth={1.3} />
          <div className="font-display text-xl text-navy">Noch keine Rechnungen</div>
          <p className="text-[13px] text-navy-dark/60 mt-2">Erstellen Sie die erste Rechnung über den Button oben.</p>
        </div>
      ) : shownCount === 0 ? (
        <div className="bg-white border border-dashed border-navy/20 p-10 text-center" data-testid="team-invoices-no-match">
          <div className="font-display text-lg text-navy">Keine Rechnung entspricht Ihrer Suche</div>
          <p className="text-[13px] text-navy-dark/60 mt-1">Bitte Suchbegriff oder Filter anpassen.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="team-invoices-list">
          {grouped.map(([year, list]) => (
            <section key={year} data-testid={`team-invoices-year-${year}`}>
              <div className="flex items-baseline gap-3 mb-3 px-1">
                <h2 className="font-display text-2xl text-navy">{year}</h2>
                <span className="label-caps text-navy-dark/50">{list.length} Rechnung{list.length === 1 ? "" : "en"}</span>
                <div className="flex-1 border-b border-navy/10 ml-2"></div>
              </div>
              <ul className="space-y-2">
                {list.map((inv) => (
                  <li key={inv.id} className="bg-white border border-navy/10 p-4 md:p-5 flex items-center gap-4 flex-wrap md:flex-nowrap" data-testid={`team-invoice-row-${inv.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-display text-lg text-navy leading-tight truncate">{inv.title}</div>
                        <StatusBadge status={inv.status} />
                      </div>
                      <div className="text-[11.5px] text-navy-dark/60 mt-1 truncate">
                        <span className="font-medium text-navy-dark/80">{inv.number}</span>
                        {" · "}{inv.customer_name || "Kunde"}
                        {" · "}{fmtDate(inv.issued_at)}
                        {inv.service_period_start && inv.service_period_end
                          ? ` · Zeitraum ${fmtDate(inv.service_period_start)} → ${fmtDate(inv.service_period_end)}`
                          : ""}
                        {inv.due_date ? ` · fällig ${fmtDate(inv.due_date)}` : ""}
                        {inv.status === "paid" && inv.payment_method
                          ? ` · bezahlt am ${fmtDate(inv.paid_at)} · ${paymentMethodLabel(inv.payment_method)}`
                          : ""}
                      </div>
                    </div>
                    <div className="font-display text-lg text-navy shrink-0 md:min-w-[110px] md:text-right" data-testid={`team-invoice-total-${inv.id}`}>{fmtMoney(inv.total, inv.currency)}</div>
                    <a href={`${API}/invoices/${inv.id}/pdf`} target="_blank" rel="noreferrer"
                      data-testid={`team-invoice-pdf-${inv.id}`}
                      className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-1.5">
                      <FileDown className="w-3.5 h-3.5" /> PDF
                    </a>
                    {inv.status !== "paid" && inv.status !== "cancelled" && (
                      <button onClick={() => markPaid(inv)}
                        data-testid={`team-invoice-pay-${inv.id}`}
                        className="text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-1.5">
                        Bezahlt
                      </button>
                    )}
                    {inv.type !== "credit_note" && (
                      <button onClick={() => duplicate(inv)}
                        data-testid={`team-invoice-duplicate-${inv.id}`}
                        title="Als Vorlage duplizieren"
                        className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-navy border border-navy/25 hover:border-gold px-3 py-1.5 transition-colors">
                        <Copy className="w-3.5 h-3.5" strokeWidth={2} /> Duplizieren
                      </button>
                    )}
                    {inv.type !== "credit_note" && inv.status !== "cancelled" && (
                      <button onClick={() => cancelInvoice(inv)}
                        data-testid={`team-invoice-cancel-${inv.id}`}
                        title="Storno-Gutschrift erzeugen (XR-Nummer)"
                        className="inline-flex items-center gap-1 text-[11px] tracking-[0.15em] uppercase text-red-800 border border-red-800/40 hover:border-red-800 hover:bg-red-50 px-3 py-1.5 transition-colors">
                        <Ban className="w-3.5 h-3.5" strokeWidth={2} /> Storno
                      </button>
                    )}
                    <button onClick={() => del(inv)} title="Löschen"
                      data-testid={`team-invoice-delete-${inv.id}`}
                      className="text-navy-dark/50 hover:text-red-600 p-1.5">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}

      <CreateInvoiceModal open={modalOpen} onClose={() => setModalOpen(false)} customers={customers}
        onCreated={(inv) => setRows((cur) => [{ ...inv, customer_name: customers.find((c) => c.id === inv.customer_id)?.name || "" }, ...(cur || [])])} />
      <PaymentModal open={!!payTarget} invoice={payTarget} onClose={() => setPayTarget(null)} onConfirm={confirmPayment} />
      {pinPendingInvoice && !pinUnlocked && (
        <AdminPinPromptDialog
          title="Zahlung bestätigen"
          description={`Bitte den 4-stelligen Admin-PIN eingeben, um die Zahlung für Rechnung ${pinPendingInvoice.number} zu bestätigen.`}
          purpose="invoice-pay"
          onClose={() => setPinPendingInvoice(null)}
          onSuccess={onPinSuccess}
        />
      )}    </div>
  );
}
